<template>
  <div id="wrapper">
    <div class="sign-in">
      <router-link :to="{ name: 'login' }">
        <b-button type="submit" variant="outline-primary">Sign in</b-button>
      </router-link>
    </div>

    <div class="sign-up">
      <LogoDescription :data="{ title: 'Recover Password', subtitle: stepSubtitle }" :published="true" />

      <div class="form">
        <!-- STEP 1 : email -->
        <form class="form-number" v-if="step === 1" @submit.prevent="submitEmail">
          <span class="text-danger" v-if="showFormError">{{ formError }}</span>
          <p class="recover-help">
            Enter the email address on your Amejpay account and we will send you a
            {{ codeLength }}-digit reset code.
          </p>

          <input
            type="text"
            v-model.trim="form.email"
            name="email"
            :class="fieldError('email') ? 'errors' : ''"
            autocomplete="email"
            required
          />
          <label alt="Email Address" placeholder="Email Address"></label>
          <span class="text-danger" v-if="fieldError('email')">{{ fieldError('email') }}</span>
        </form>

        <!-- STEP 2 : code + new password -->
        <form class="form-number" v-else-if="step === 2" @submit.prevent="submitReset">
          <span class="text-danger" v-if="showFormError">{{ formError }}</span>
          <p class="recover-help">
            We sent a {{ codeLength }}-character code to <strong>{{ sentTo }}</strong>.
            It expires in {{ expiresIn }} minutes.
          </p>

          <input
            type="text"
            v-model.trim="form.token"
            name="token"
            inputmode="text"
            :maxlength="codeLength"
            :class="fieldError('token') ? 'errors' : ''"
            autocomplete="one-time-code"
            required
          />
          <label alt="Reset Code" placeholder="Reset Code"></label>
          <span class="text-danger" v-if="fieldError('token')">{{ fieldError('token') }}</span>

          <input
            type="password"
            v-model="form.password"
            name="password"
            :class="fieldError('password') ? 'errors' : ''"
            autocomplete="new-password"
            required
          />
          <label alt="New Password" placeholder="New Password"></label>
          <span class="text-danger" v-if="fieldError('password')">{{ fieldError('password') }}</span>

          <!-- Live requirements beat a rejection after the fact.
               Deliberately NOT a <ul>: the login stylesheet sets
                   .form form ul { display: flex }
               for the PIN screen's digit boxes, which turned this checklist into a
               horizontal row that ran across the Confirm Password field. Plain divs
               are out of that rule's reach. -->
          <div class="pw-rules" v-if="form.password">
            <div class="pw-rule" v-for="rule in passwordRules" :key="rule.key" :class="rule.ok ? 'ok' : ''">
              <span class="material-icons">{{ rule.ok ? 'check_circle' : 'radio_button_unchecked' }}</span>
              <span>{{ rule.text }}</span>
            </div>
          </div>

          <input
            type="password"
            v-model="form.confirm_password"
            name="confirm_password"
            :class="fieldError('confirm_password') ? 'errors' : ''"
            autocomplete="new-password"
            required
          />
          <label alt="Confirm Password" placeholder="Confirm Password"></label>
          <span class="text-danger" v-if="fieldError('confirm_password')">
            {{ fieldError('confirm_password') }}
          </span>

          <div class="resend-row">
            <span v-if="cooldown > 0" class="muted">Resend available in {{ cooldown }}s</span>
            <a href="#" v-else @click.prevent="resendCode">Send a new code</a>
            <a href="#" class="ml-3" @click.prevent="startOver">Use a different email</a>
          </div>
        </form>

        <!-- STEP 3 : done -->
        <div class="recover-done" v-else>
          <span class="material-icons done-icon">check_circle</span>
          <h5>Password updated</h5>
          <p>You can now sign in with your new password.</p>
        </div>
      </div>

      <div class="next-button">
        <router-link :to="{ name: 'login' }" v-if="step !== 3">
          <b-button type="button" variant="outline-none">Sign in</b-button>
        </router-link>
        <b-button
          v-if="step === 1"
          type="submit"
          variant="amej-button"
          :disabled="isLoading"
          @click="submitEmail"
        >{{ isLoading ? 'Sending...' : 'Next' }}</b-button>
        <b-button
          v-else-if="step === 2"
          type="submit"
          variant="amej-button"
          :disabled="isLoading"
          @click="submitReset"
        >{{ isLoading ? 'Saving...' : 'Reset Password' }}</b-button>
        <b-button
          v-else
          type="button"
          variant="amej-button"
          @click="goToLogin"
        >Sign in</b-button>
      </div>
    </div>
    <Footer></Footer>
  </div>
</template>

<script>
import LogoDescription from "../reuseable_components/LogoDescription";
import Footer from "../reuseable_components/Footer";
import { mapActions, mapState } from "vuex";

const CODE_LENGTH = 6;
/** Reset codes are alphanumeric, e.g. "A0ADA0". Case is left alone - the server decides. */
const CODE_RE = /^[A-Za-z0-9]+$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export default {
  name: "ForgetPassword",
  components: { LogoDescription, Footer },
  data() {
    return {
      step: 1,
      isLoading: false,
      codeLength: CODE_LENGTH,
      sentTo: "",
      expiresIn: 10,
      cooldown: 0,
      cooldownTimer: null,
      formError: "",
      errors: {},
      form: { email: "", token: "", password: "", confirm_password: "" },
    };
  },
  computed: {
    ...mapState(["registerErrors", "successmessage", "errormessage", "message"]),
    stepSubtitle: function () {
      if (this.step === 1) { return "Enter your email address"; }
      if (this.step === 2) { return "Enter the code and a new password"; }
      return "All done";
    },
    /** Live password requirements - shown while typing, not after rejection. */
    passwordRules: function () {
      const p = this.form.password || "";
      return [
        { key: "len", text: "At least 8 characters", ok: p.length >= 8 },
        { key: "upper", text: "One uppercase letter", ok: /[A-Z]/.test(p) },
        { key: "lower", text: "One lowercase letter", ok: /[a-z]/.test(p) },
        { key: "digit", text: "One number", ok: /\d/.test(p) },
        { key: "special", text: "One special character", ok: /[^a-zA-Z0-9]/.test(p) },
        { key: "space", text: "No spaces", ok: p.length > 0 && !/\s/.test(p) },
      ];
    },
    passwordValid: function () {
      return this.passwordRules.every(function (r) { return r.ok; });
    },
    /** Suppress the banner when the same sentence is already under a field. */
    showFormError: function () {
      if (!this.formError) { return false; }
      const merged = Object.assign({}, this.registerErrors || {}, this.errors || {});
      const texts = Object.keys(merged).map(function (k) { return String(merged[k] || ""); });
      return texts.indexOf(String(this.formError)) === -1;
    },
  },
  beforeDestroy() {
    if (this.cooldownTimer) { clearInterval(this.cooldownTimer); }
  },
  methods: {
    ...mapActions(["forgetpassword", "resetpassword"]),

    fieldError(name) {
      if (this.errors[name]) { return this.errors[name]; }
      const server = this.registerErrors || {};
      // The API may name it either way round.
      if (name === "token") { return server.token || server.otp || ""; }
      if (name === "password") { return server.password || server.new_password || ""; }
      return server[name] || "";
    },
    clearMessages() {
      this.errors = {};
      this.formError = "";
      this.$store.commit("setRegisterErrors", {});
    },

    startCooldown(seconds) {
      const vm = this;
      this.cooldown = seconds || 60;
      if (this.cooldownTimer) { clearInterval(this.cooldownTimer); }
      this.cooldownTimer = setInterval(function () {
        vm.cooldown = vm.cooldown - 1;
        if (vm.cooldown <= 0) { clearInterval(vm.cooldownTimer); vm.cooldownTimer = null; }
      }, 1000);
    },

    validateEmail() {
      const e = {};
      if (!this.form.email) { e.email = "Email address is required."; }
      else if (!EMAIL_RE.test(this.form.email)) { e.email = "Please enter a valid email address."; }
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    validateReset() {
      const e = {};
      const token = String(this.form.token || "").trim();
      if (!token) {
        e.token = "The reset code is required.";
      } else if (!CODE_RE.test(token)) {
        // The server issues codes like "A0ADA0" - letters AND digits, so a digits-only
        // rule rejected perfectly valid codes before they were ever sent.
        e.token = "The code is letters and numbers only.";
      } else if (token.length !== CODE_LENGTH) {
        e.token = "Please enter the " + CODE_LENGTH + "-character code.";
      }

      if (!this.form.password) {
        e.password = "A new password is required.";
      } else if (!this.passwordValid) {
        e.password = "Please meet all the password requirements below.";
      }

      if (!this.form.confirm_password) {
        e.confirm_password = "Please confirm the new password.";
      } else if (this.form.confirm_password !== this.form.password) {
        e.confirm_password = "The two passwords do not match.";
      }
      this.errors = e;
      return Object.keys(e).length === 0;
    },

    submitEmail() {
      if (this.isLoading) { return; }
      this.clearMessages();
      if (!this.validateEmail()) { return; }

      const vm = this;
      this.isLoading = true;
      return this.forgetpassword({ email: this.form.email })
        .then(function (res) {
          vm.isLoading = false;
          if (res && res.ok) {
            vm.sentTo = res.email || vm.form.email;
            vm.expiresIn = res.expiresIn || 10;
            vm.step = 2;
            vm.form.token = "";
            vm.startCooldown(60);
          } else {
            vm.formError = (res && res.message) || "Unable to send a reset code.";
          }
        })
        .catch(function () {
          vm.isLoading = false;
          vm.formError = "Unable to send a reset code. Please try again.";
        });
    },

    submitReset() {
      if (this.isLoading) { return; }
      this.clearMessages();
      if (!this.validateReset()) { return; }

      const vm = this;
      this.isLoading = true;
      return this.resetpassword({
        token: this.form.token,
        password: this.form.password,
        confirm_password: this.form.confirm_password,
      })
        .then(function (res) {
          vm.isLoading = false;
          if (res && res.ok) {
            vm.step = 3;
            vm.form.password = "";
            vm.form.confirm_password = "";
            vm.form.token = "";
            // Land on the sign-in screen without stranding anyone who ignores it.
            setTimeout(function () { vm.goToLogin(); }, 2500);
          } else {
            vm.formError = (res && res.message) || "Unable to reset the password.";
          }
        })
        .catch(function () {
          vm.isLoading = false;
          vm.formError = "Unable to reset the password. Please try again.";
        });
    },

    resendCode() {
      if (this.cooldown > 0 || this.isLoading) { return; }
      const vm = this;
      this.clearMessages();
      this.isLoading = true;
      return this.forgetpassword({ email: this.sentTo || this.form.email })
        .then(function (res) {
          vm.isLoading = false;
          // The cooldown starts either way - the server's own limit is running.
          vm.startCooldown(60);
          vm.formError = (res && res.ok) ? "" : ((res && res.message) || "");
        })
        .catch(function () { vm.isLoading = false; vm.startCooldown(60); });
    },

    startOver() {
      this.clearMessages();
      this.step = 1;
      this.form.token = "";
      this.form.password = "";
      this.form.confirm_password = "";
      if (this.cooldownTimer) { clearInterval(this.cooldownTimer); this.cooldownTimer = null; }
      this.cooldown = 0;
    },

    goToLogin() {
      this.$router.replace({ name: "login", query: { password: "success" } })
        .catch(function () {});
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../css/style.scss";
</style>
<style scoped>
/* `.form` in the login section is `display: grid` with four columns (again, for the
   PIN screen). Force this screen's form back to normal block flow so the banner,
   fields and checklist stack instead of tiling across four columns. */
.form >>> form,
.form form {
  display: block;
  width: 100%;
}
.recover-help {
  font-size: 13px;
  color: #6b7280;
  line-height: 1.5;
  margin-bottom: 18px;
}
.pw-rules {
  display: block;
  list-style: none;
  padding: 10px 12px;
  margin: -4px 0 18px;
  background: #f7f9fc;
  border: 1px solid #eceff4;
  border-radius: 8px;
}
.pw-rules .pw-rule {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #8a94a6;
  line-height: 1.7;
  white-space: nowrap;
}
.pw-rules .pw-rule.ok { color: #1e8c50; }
.pw-rules .material-icons { font-size: 14px; margin-right: 6px; flex: 0 0 auto; }

/* Two columns once there is room, so six rules do not push the button off screen. */
@media (min-width: 420px) {
  .pw-rules {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 12px;
  }
}
.resend-row {
  font-size: 13px;
  margin-top: 4px;
}
.resend-row .muted { color: #8a94a6; }
.recover-done {
  text-align: center;
  padding: 20px 0 10px;
}
.recover-done .done-icon {
  font-size: 46px;
  color: #28a745;
  margin-bottom: 10px;
}
.recover-done h5 { margin-bottom: 6px; }
.recover-done p { font-size: 14px; color: #6b7280; }
</style>
